import { useEffect, useRef } from 'react';

const STATE_KEY = '__closeOnBackNavigationSeq';

// Shared across every instance of this hook so that concurrently active,
// nested layers (e.g. an overlay containing a wizard scene containing a
// sub-scene) each get a strictly increasing, globally unique sequence
// number. `popstate` is a single global event with no notion of "layers" —
// this is what lets each instance work out whether *its own* entry was the
// one just removed, see `handlePopState` below.
let sharedSequence = 0;

type PendingCleanup = {
  cancelled: boolean;
  timer: ReturnType<typeof setTimeout>;
};

/**
 * While `active` is true, keeps exactly one browser history entry pushed so a
 * hardware/gesture back closes only this layer (by calling `onClose`) instead
 * of navigating the whole page away, which matters for stacked overlays where
 * nothing else would ever give the back button something of ours to pop. The
 * pushed entry is reconciled no matter how the overlay closes — via `onClose`
 * being triggered by `popstate`, via on-screen UI flipping `active` back to
 * `false`, or via unmounting outright — so a history entry never lingers as a
 * dead back-stop.
 *
 * Every instance tags its own entry with a unique sequence number and only
 * reacts to a `popstate` that actually consumed its own entry (current state
 * carries a lower sequence than the one it pushed). Without this, closing an
 * inner layer via on-screen UI — which cleans up by calling `history.back()`
 * — would fire a `popstate` that every still-active outer layer also hears,
 * incorrectly cascading their `onClose` too and collapsing the whole stack.
 *
 * That cleanup's `history.back()` is deferred by a tick rather than called
 * synchronously, and is cancelled if `active` flips back to `true` before it
 * runs (reusing the still-current entry instead of popping and re-pushing).
 * `history.back()` resolves against whatever is *actually current* at the
 * moment it executes, not at the moment it was called — so without this, a
 * fast close-then-reopen would push a fresh entry on top before the earlier,
 * still-pending `back()` fires, and that `back()` would then pop the *new*
 * entry instead of the stale one it was meant for: silently closing the
 * reactivated layer and leaving the real history stack short one entry.
 */
const useCloseOnBackNavigation = (active: boolean, onClose: () => void) => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const consumedByPopStateRef = useRef(false);
  const ownSequenceRef = useRef<number | null>(null);
  const pendingCleanupRef = useRef<PendingCleanup | null>(null);

  useEffect(() => {
    if (!active) return undefined;

    if (pendingCleanupRef.current) {
      // Reactivated before the previous close's deferred history.back() ran:
      // cancel it and keep using the entry it would have popped — the real
      // history never actually moved, so there is nothing to reconcile.
      pendingCleanupRef.current.cancelled = true;
      clearTimeout(pendingCleanupRef.current.timer);
      pendingCleanupRef.current = null;
    } else {
      ownSequenceRef.current = sharedSequence += 1;
      window.history.pushState({ [STATE_KEY]: ownSequenceRef.current }, '');
    }

    const ownSequence = ownSequenceRef.current;
    consumedByPopStateRef.current = false;

    const handlePopState = (event: PopStateEvent) => {
      const currentSequence =
        (event.state as Record<string, number> | null)?.[STATE_KEY] ?? 0;
      // A lower (or absent) sequence means our own entry is the one that was
      // just popped. An equal-or-higher sequence means some layer nested
      // inside us was popped instead — not our concern.
      if (currentSequence >= ownSequence!) return;

      consumedByPopStateRef.current = true;
      onCloseRef.current();
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Closed via on-screen UI (or unmounted outright) rather than via back:
      // consume the entry we pushed so it never lingers as a dead back-stop.
      // Deferred (see doc comment above) so a fast reactivation can cancel it.
      if (!consumedByPopStateRef.current) {
        const pending: PendingCleanup = {
          cancelled: false,
          timer: setTimeout(() => {
            if (!pending.cancelled) {
              window.history.back();
            }
          }, 0),
        };
        pendingCleanupRef.current = pending;
      }
    };
  }, [active]);
};

export { useCloseOnBackNavigation };
